import type { Module } from 'vuex';
import type {Theme} from "@/store/modules/theme";
import { LOCAL_STORAGE_KEYS, setLocalStorageValue, removeLocalStorageValue, getLocalStorageValue } from '@/constants/localStorage';
import {RootState} from "@/store";

export interface AuthState {
  authMode: string | null;
  user: {
    idUser: number | null;
    email: string | null;
    name: string | null;
    theme: Theme;
    admin: boolean;
    confirmEmail: boolean;
    willDelete: number | null;
    idLanguage: number | null;
    idLocale: number | null;
    newPasswordExists?: boolean;
    register?: {
      emailProgressMessage: string | null;
      emailProgressType: string | null;
      success: boolean | null;
    };
    newPassword?: {
      emailProgressMessage: string | null;
      emailProgressType: string | null;
      code: boolean | null;
      success: number | null;
    };
    restorePassword?: {
      emailProgressMessage: string | null;
      emailProgressType: string | null;
      confirm: boolean;
      success: number | null;
    };
    changeEmail?: {
      emailProgressMessage: string | null;
      emailProgressType: string | null;
      newEmail: string | null;
      success: number | null;
    };
  };
  welcomeScreen: boolean;
  restorePasswordEmail: string;
}

const authModule: Module<AuthState, RootState> = {
  namespaced: true,

  state: (): AuthState => ({
    authMode: null,
    user: {
      idUser: 0,
      email: '',
      name: '',
      theme: null,
      admin: false,
      confirmEmail: false,
      willDelete: null,
      idLanguage: null,
      idLocale: null,
    },
    welcomeScreen: false,
    restorePasswordEmail: '',
  }),

  getters: {
    isSessionInit: (state): boolean => state.user.idUser !== 0,
    isUserEmailConfirm: (state): boolean => state.user.confirmEmail && !state.welcomeScreen,
  },

  mutations: {
    setAuthMode(state, payload) {
      if (['login', 'register', 'registerCode', 'registerSuccess', 'restore', 'restoreCode', 'restoreConfirm', 'restoreSuccess', null].includes(payload)) {
        state.authMode = payload;
      } else {
        state.authMode = null;
      }

      let modeStored;
      switch (state.authMode) {
        case null:
          modeStored = null;
          break;
        case 'login':
          modeStored = 'login';
          break;
        case 'registerCode':
        case 'registerSuccess':
        case 'register':
          modeStored = 'register';
          break;
        case 'restoreCode':
        case 'restoreConfirm':
        case 'restoreSuccess':
        case 'restore':
          modeStored = 'restore';
          break;
        default:
          modeStored = null;
          break;
      }
      switch (state.authMode) {
        case 'register':
          if (state.user.idUser !== null) {
            if ((!state.user.confirmEmail) && (state.user.register && state.user.register.success === null)) {
              state.authMode = 'registerCode';
            } else if (state.welcomeScreen) {
              state.authMode = 'registerSuccess';
            }
          }
          break;
        case 'restore':
          if ((state.user.idUser === null) && (state.restorePasswordEmail !== '')) {
            if (!state.user.restorePassword?.confirm) {
              state.authMode = 'restoreCode';
            } else if (state.user.restorePassword?.success === null) {
              state.authMode = 'restoreConfirm';
            } else {
              state.authMode = 'restoreSuccess';
            }
          }
          break;
      }

      if (!['login', 'register', 'restore', null].includes(modeStored)) {
        modeStored = null;
      }
      if (modeStored === null) {
        removeLocalStorageValue(LOCAL_STORAGE_KEYS.AUTH_MODE);
      } else if (getLocalStorageValue(LOCAL_STORAGE_KEYS.AUTH_MODE) !== modeStored) {
        setLocalStorageValue(LOCAL_STORAGE_KEYS.AUTH_MODE, modeStored);
      }
    },

    setUser(state, payload) {
      state.user.idUser = payload.idUser;
      state.user.email = payload.email;
      state.user.name = payload.name;
      state.user.theme = payload.theme;
      state.user.admin = payload.admin;
      state.user.confirmEmail = payload.confirmEmail;
      state.user.willDelete = payload.willDelete;
      state.user.idLanguage = payload.idLanguage;
      state.user.idLocale = payload.idLocale;

      state.user.register = payload.register;
      state.user.newPassword = payload.newPassword;
      state.user.restorePassword = payload.restorePassword;
      state.user.changeEmail = payload.changeEmail;

      if (state.user.idUser === null && state.user.email !== null) {
        state.restorePasswordEmail = state.user.email;
      } else {
        state.restorePasswordEmail = '';
      }
    },

    unsetUser(state) {
      state.user.idUser = null;
      state.user.email = '';
      state.user.name = '';
      state.user.admin = false;
      state.user.confirmEmail = false;
      state.user.idLanguage = null;
      state.user.idLocale = null;

      state.user.register = {
        emailProgressMessage: null,
        emailProgressType: null,
        success: null,
      };
      state.user.newPassword = {
        emailProgressMessage: null,
        emailProgressType: null,
        code: null,
        success: null,
      };
      state.user.restorePassword = {
        emailProgressMessage: null,
        emailProgressType: null,
        confirm: false,
        success: null,
      };
      state.user.changeEmail = {
        emailProgressMessage: null,
        emailProgressType: null,
        newEmail: null,
        success: null,
      };

      state.restorePasswordEmail = '';
    },

    setWelcomeScreen(state, payload) {
      state.welcomeScreen = payload;
    },

    profileSetName(state, payload) {
      state.user.name = payload;
    },

    profileSetTheme(state, payload) {
      state.user.theme = payload;
    },

    setUserTheme(state, payload) {
      state.user.theme = payload;
    },

    profileSetLocale(state, payload) {
      if (payload === null || payload === undefined) {
        state.user.idLocale = null;
      } else {
        state.user.idLocale = payload;
      }
    },

    profileSetLanguage(state, payload) {
      if (payload === null || payload === undefined) {
        state.user.idLanguage = null;
      } else {
        state.user.idLanguage = payload;
      }
    },

    registerConfirm(state) {
      state.user.confirmEmail = true;
    },

    restore(state, payload) {
      state.user.restorePassword = {
        emailProgressMessage: null,
        emailProgressType: null,
        confirm: false,
        success: null,
      };
      state.restorePasswordEmail = payload;
    },

    restoreConfirm(state) {
      state.restorePasswordEmail = '';
      if (state.user.restorePassword) {
        state.user.restorePassword.confirm = true;
      }
    },

    restoreSuccess(state) {
      if (state.user.restorePassword) {
        state.user.restorePassword.success = Math.floor(Date.now() / 1000);
      }
    },

    newPasswordRequest(state) {
      state.user.newPassword = {
        emailProgressMessage: null,
        emailProgressType: null,
        code: true,
        success: null,
      };
      state.user.newPasswordExists = true;
    },

    newPasswordEmailProgress(state, payload) {
      if (state.user.newPassword) {
        state.user.newPassword.emailProgressMessage = payload.message;
        state.user.newPassword.emailProgressType = payload.type;
      }
    },

    newPasswordCancel(state) {
      state.user.newPassword = {
        emailProgressMessage: null,
        emailProgressType: null,
        code: null,
        success: null,
      };
      state.user.newPasswordExists = false;
    },

    newPasswordConfirm(state, payload) {
      if (state.user.newPassword) {
        state.user.newPassword.code = payload;
        state.user.newPassword.success = Math.floor(Date.now() / 1000);
      }
    },

    changeEmailRequest(state, payload) {
      state.user.changeEmail = {
        emailProgressMessage: null,
        emailProgressType: null,
        newEmail: payload,
        success: null,
      };
    },

    changeEmailEmailProgress(state, payload) {
      if (state.user.changeEmail) {
        state.user.changeEmail.emailProgressMessage = payload.message;
        state.user.changeEmail.emailProgressType = payload.type;
      }
    },

    changeEmailCancel(state) {
      state.user.changeEmail = {
        emailProgressMessage: null,
        emailProgressType: null,
        newEmail: null,
        success: null,
      };
    },

    changeEmailConfirm(state) {
      if (state.user.changeEmail) {
        state.user.changeEmail.success = Math.floor(Date.now() / 1000);
        state.user.email = state.user.changeEmail.newEmail;
      }
    },

    adminUserUpdate(state, payload) {
      state.user.name = payload.name;
      state.user.admin = payload.admin;
      // Note: block property is not part of user interface, ignoring it
    },

    updateUserWillDelete(state, payload) {
      state.user.willDelete = payload.willDelete;
    },
  },

  actions: {
    setAuthMode({ commit }, payload) {
      commit('setAuthMode', payload);
    },

    setUser({ commit, state }, payload) {
      commit('setUser', {
        idUser: payload.idUser,
        email: payload.email,
        name: payload.name,
        theme: payload.theme,
        admin: payload.admin,
        confirmEmail: payload.confirmEmail,
        willDelete: payload.willDelete ?? null,
        idLanguage: payload.idLanguage,
        idLocale: payload.idLocale,
        register: payload.register ?? {
          emailProgressMessage: null,
          emailProgressType: null,
          success: null,
        },
        newPassword: payload.newPassword ?? {
          emailProgressMessage: null,
          emailProgressType: null,
          code: null,
          success: null,
        },
        restorePassword: payload.restorePassword ?? {
          emailProgressMessage: null,
          emailProgressType: null,
          confirm: false,
          success: null,
        },
        changeEmail: payload.changeEmail ?? {
          emailProgressMessage: null,
          emailProgressType: null,
          newEmail: null,
          success: null,
        },
      });

      commit('setAuthMode', state.authMode);
    },

    unsetUser({ commit }) {
      commit('unsetUser');
      commit('setAuthMode', 'login');
    },

    setWelcomeScreen({ commit }, payload) {
      commit('setWelcomeScreen', payload);
    },

    profileSetName({ commit }, payload) {
      commit('profileSetName', payload);
    },

    profileSetTheme({ commit }, payload) {
      commit('profileSetTheme', payload);
    },

    profileSetLocale({ commit }, payload) {
      if (payload && typeof payload === 'object' && 'idLocale' in payload) {
        commit('profileSetLocale', payload.idLocale);
      } else if (payload === null || payload === undefined) {
        commit('profileSetLocale', null);
      } else {
        commit('profileSetLocale', payload);
      }
    },

    profileSetLanguage({ commit }, payload) {
      if (payload && typeof payload === 'object' && 'idLanguage' in payload) {
        commit('profileSetLanguage', payload.idLanguage);
      } else if (payload === null || payload === undefined) {
        commit('profileSetLanguage', null);
      } else {
        commit('profileSetLanguage', payload);
      }
    },

    profileNewPasswordRequest({ commit }) {
      commit('newPasswordRequest');
    },

    profileNewPasswordEmailProgress({ commit }, payload) {
      commit('newPasswordEmailProgress', payload);
    },

    profileNewPasswordCancel({ commit }) {
      commit('newPasswordCancel');
    },

    profileNewPasswordConfirm({ commit }, payload) {
      commit('newPasswordConfirm', payload);
    },

    profileChangeEmailRequest({ commit }, payload) {
      commit('changeEmailRequest', payload);
    },

    profileChangeEmailEmailProgress({ commit }, payload) {
      commit('changeEmailEmailProgress', payload);
    },

    profileChangeEmailCancel({ commit }) {
      commit('changeEmailCancel');
    },

    profileChangeEmailConfirm({ commit }) {
      commit('changeEmailConfirm');
    },

    adminUserUpdate({ commit }, payload) {
      commit('adminUserUpdate', {
        name: payload.name,
        admin: payload.admin,
        block: payload.block,
      });
    },

    registerConfirm({ commit }) {
      commit('registerConfirm');
    },

    restore({ commit }, payload) {
      commit('restore', payload);
    },

    restoreConfirm({ commit }) {
      commit('restoreConfirm');
    },

    restoreSuccess({ commit }) {
      commit('restoreSuccess');
    },

    updateUserWillDelete({ commit }, payload) {
      commit('updateUserWillDelete', payload);
    },

    wsSetUser({ dispatch, state, rootState }, payload) {
      dispatch('setUser', payload);
      if (payload.idUser !== 0 && payload.idUser !== null) {
        if (payload.theme !== rootState.theme.theme) {
          dispatch('theme/setTheme', payload.theme, { root: true });
        }
        // Обновляем тему пользователя в auth state, если она отличается от текущей темы
        if (payload.theme !== rootState.theme.theme) {
          dispatch('setUserTheme', rootState.theme.theme);
        }
      }
    },

    wsUnsetUser({ dispatch }) {
      dispatch('unsetUser');
    },
  },
};

export default authModule; 